/**
 * Hero Search Bar Component
 *
 * Large search bar shown on landing page below the navbar as a separate component.
 * Figma: HeroSearch (Node: 595:4644)
 *
 * Dimensions:
 * - Container: Full width, centered content at 900px
 * - Search Bar: 900px × 60px (centered)
 * - Padding: 110px top, 64px gap to bottom divider
 *
 * States:
 * - Default (Inactive): Simple search bar with placeholder
 * - Active (Expanded): Shows popular searches dropdown with suggestions
 *
 * Component Structure (matching Figma):
 * - HeroSearchBar (this file) - Main container
 * - WideSearchBar - Reusable 900px search input
 * - SuggestionContainer - Dropdown content container
 *   - SearchSuggestion - Individual suggestion items
 *
 * Dropdown Spacing (exact Figma specs):
 * - Top padding: 40px
 * - Gap between sections: 32px (handled by SuggestionContainer)
 * - Bottom divider: 64px gap from search container
 *
 * Border: Full-width bottom divider, #d1d5dc, 1px (only when expanded)
 */
"use client";

import { useEffect, useRef, useState } from "react";

import { useRouter } from "next/navigation";

import { v4 as uuidv4 } from "uuid";

import "@/app/globals.css";
import { WideSearchBar } from "@/components/shared/WideSearchBar";
import { useUserGender } from "@/hooks/useUserGender";
import { BRAND_EXPLORER_PATH } from "@/lib/route-paths";
import { SECTION_PADDING_X, SECTION_PADDING_Y, SECTION_GAP } from "@/styles/responsive-spacing";

import {
  getActiveSearchSuggestions,
  type SearchSuggestionCategory,
} from "./search-suggestion-config";
import { SuggestionContainer } from "./SuggestionContainer";

interface HeroSearchBarProps {
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: (gender?: string, query?: string, chatId?: string) => void;
  onFocus?: () => void;
  placeholder?: string;
  className?: string;
}

export function HeroSearchBar({
  value = "",
  onChange,
  onSubmit,
  onFocus,
  placeholder = "What are you looking for?",
  className = "",
}: HeroSearchBarProps) {
  const router = useRouter();
  const userGender = useUserGender();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [selectedGender, setSelectedGender] = useState<SearchSuggestionCategory>("Women's");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  // Track if user has manually selected a gender to prevent overwriting
  const hasManuallySelectedGender = useRef(false);

  // Get appropriate suggestions based on selected category
  const currentSuggestions = getActiveSearchSuggestions(selectedGender);
  // Map database gender values to UI display values
  const mapGenderToDisplay = (dbGender: string | null): SearchSuggestionCategory => {
    switch (dbGender) {
      case "mens":
        return "Men's";
      case "womens":
        return "Women's";
      case "unisex":
        return "Both (M+W)"; // Map unisex to Both option
      default:
        return "Women's"; // Default for null or unknown values
    }
  };

  // Update selected gender when user gender loads (only if user hasn't manually selected)
  useEffect(() => {
    if (userGender && !hasManuallySelectedGender.current) {
      setSelectedGender(mapGenderToDisplay(userGender));
    }
  }, [userGender]);

  const closeDropdown = () => {
    setIsClosing(true);
    closeTimeoutRef.current = setTimeout(() => {
      setIsExpanded(false);
      setIsClosing(false);
    }, 250); // Match animation duration
  };

  const openDropdown = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setIsClosing(false);
    setIsExpanded(true);
  };

  const submitQuery = (query: string) => {
    const chatId = uuidv4();
    onSubmit?.(selectedGender, query, chatId);
    closeDropdown();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const query = value?.trim();
      if (query) {
        submitQuery(query);
      }
    } else if (e.key === "Escape") {
      closeDropdown();
    }
  };

  const handleFocus = () => {
    openDropdown();
    onFocus?.();
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const handleSuggestionClick = (suggestion: string) => {
    submitQuery(suggestion);
  };

  const handleExploreBrandsClick = () => {
    router.push(BRAND_EXPLORER_PATH);
    closeDropdown();
  };

  const handleGenderSelect = (gender: SearchSuggestionCategory) => {
    hasManuallySelectedGender.current = true;
    setSelectedGender(gender);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`flex w-full flex-col items-center bg-white ${className}`}>
      {/* HeroSearch Container - Figma Node: 595:4644 */}
      <div
        className="flex w-full flex-col items-center"
        style={{
          paddingInline: SECTION_PADDING_X.RESPONSIVE,
          paddingTop: SECTION_PADDING_Y.HERO,
          gap: SECTION_GAP.SMALL,
        }}
      >
        {/* Search Bar Container - 900px centered (Figma Node: 595:4645) */}
        <div
          ref={containerRef}
          className="flex w-full cursor-text flex-col items-start"
          style={{ width: "100%", maxWidth: "900px" }}
          onClick={handleContainerClick}
        >
          {/* Wide Search bar - Figma Node: 595:4646 */}
          <WideSearchBar
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onSubmit={() => {
              const query = value?.trim();
              if (query) {
                submitQuery(query);
              }
            }}
            placeholder={placeholder}
            inputRef={inputRef}
            isFocused={isExpanded}
          />

          {/* Drop down - only visible when expanded (Figma Node: 595:4647) */}
          {isExpanded && (
            <div
              className="flex w-full flex-col items-start overflow-clip"
              style={{
                paddingTop: "40px",
                animation: isClosing ? "slideUp 0.25s ease-in-out" : "slideDown 0.25s ease-in-out",
              }}
            >
              <SuggestionContainer
                suggestions={currentSuggestions}
                onSuggestionClick={handleSuggestionClick}
                onExploreBrandsClick={handleExploreBrandsClick}
                selectedGender={selectedGender}
                onGenderSelect={handleGenderSelect}
              />
            </div>
          )}
        </div>

        {/* Bottom Divider - match search width (Figma Node: 595:4675) */}
        {isExpanded && (
          <div className="flex w-full justify-center px-4">
            <div
              className="h-px w-full"
              style={{
                maxWidth: "900px",
                backgroundColor: "#d1d5dc",
                borderTop: "1px solid #d1d5dc",
                animation: isClosing ? "fadeOut 0.25s ease-in-out" : "fadeIn 0.25s ease-in-out",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
